import { Link } from '@tanstack/react-router';
import { PlugIcon } from 'lucide-react';
import { toast } from 'sonner';

import { DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { useCopyToClipboard } from '@/shared/hooks/useCopyToClipboard';

import type { AiToolHelp } from '../aiToolHelp';
import { AI_HELP_COPY, EDITOR_SETTINGS_PATH } from '../copy';

/**
 * A refused clipboard is silent, so it is said, with the ask itself to select:
 * the person came for the text and should leave with it (spec 248).
 */
function notCopied(ask: string) {
  toast.error(AI_HELP_COPY.notCopied, {
    description: (
      <>
        {AI_HELP_COPY.notCopiedHint} <span className="select-all">{ask}</span>
      </>
    ),
  });
}

/**
 * A menu's item for an AI tool (spec 248): the ask to copy, or the way to set a
 * tool up. The caller asks `useAiToolHelp` and leaves it out when hidden, so a
 * menu with nothing else in it can stay away too.
 */
export function AiToolMenuItem({
  help,
  ask,
}: {
  help: Exclude<AiToolHelp, 'hidden'>;
  ask: string;
}) {
  const { copy } = useCopyToClipboard();
  if (help === 'setup') {
    return (
      <DropdownMenuItem render={<Link to={EDITOR_SETTINGS_PATH} />}>
        <PlugIcon aria-hidden="true" />
        {AI_HELP_COPY.setUp}
      </DropdownMenuItem>
    );
  }
  return (
    <DropdownMenuItem
      onClick={() => {
        // The toast outlives the menu, which closes on the click.
        void copy(ask).then((copied) => {
          if (copied) toast.success(AI_HELP_COPY.copiedToast);
          else notCopied(ask);
        });
      }}
    >
      <PlugIcon aria-hidden="true" />
      {AI_HELP_COPY.taskCopy}
    </DropdownMenuItem>
  );
}

interface Words {
  before?: string;
  label: string;
  after?: string;
}

/**
 * A quiet line of text whose link copies the ask, or leads to setting a tool
 * up (spec 248). "Copied" stands in the link for a moment instead of a toast:
 * the person is looking at it.
 */
export function AiToolLine({
  help,
  ask,
  copyWords,
  setupWords,
  className,
}: {
  help: Exclude<AiToolHelp, 'hidden'>;
  ask: string;
  copyWords: Words;
  setupWords: Words;
  className?: string;
}) {
  const { copy, state } = useCopyToClipboard();
  const words = help === 'setup' ? setupWords : copyWords;
  const linkClass =
    'touch-target text-foreground underline decoration-muted-foreground/50 underline-offset-2 hover:decoration-foreground focus-visible:outline-ring rounded-sm';
  return (
    <p className={`text-muted-foreground text-xs ${className ?? ''}`}>
      {words.before}
      {help === 'setup' ? (
        <Link to={EDITOR_SETTINGS_PATH} className={linkClass}>
          {words.label}
        </Link>
      ) : (
        <button
          type="button"
          className={`${linkClass} cursor-pointer`}
          onClick={() => {
            void copy(ask).then((copied) => {
              if (!copied) notCopied(ask);
            });
          }}
        >
          {state === 'copied' ? AI_HELP_COPY.copied : words.label}
        </button>
      )}
      {words.after}
    </p>
  );
}
